import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Dropdown, Option } from "@fluentui/react-components";
import { FluentField } from "../../../../components/FluentUI/Fluent-Field";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import { errorNotification } from "../../../../redux/notifications";
import { useDispatch } from "react-redux";
import { FluentSwitch } from "../../../../components/FluentUI/Fluent-Switch";
import './sub-department.scss';

const WidgetSettings = ({ subDepartment, setSubDepartment }) => {
  const dispatch = useDispatch();

  const handleAddPair = () => {
    const { WidgetPairs } = subDepartment;
    if (
      WidgetPairs.length === 0 ||
      WidgetPairs[WidgetPairs.length - 1]?.WidgetProjectTemplate?.Id ||
      WidgetPairs[WidgetPairs.length - 1]?.WidgetTaskType?.Id
    ) {
      setSubDepartment((prev) => ({
        ...prev,
        WidgetPairs: [
          ...prev.WidgetPairs,
          {
            WidgetProjectTemplate: {},
            WidgetTaskType: {},
            SameAsDefaultPair: false,
            Order: WidgetPairs.length + 1, 
          },
        ],
      }));
    } else {
      dispatch(
        errorNotification({
          header: "Error",
          message: "Please complete at least one field (Project Template or Task Type) before adding a new one.",
        })
      );
    }
  };

  const handleDeletePair = (index) => {
    const updatedPairs = [...subDepartment.WidgetPairs];
    updatedPairs.splice(index, 1);
    
    const reorderedPairs = updatedPairs.map((pair, idx) => ({
      ...pair,
      Order: idx + 1,
    }));

    setSubDepartment((prev) => ({
      ...prev,
      WidgetPairs: reorderedPairs,
    }));
  };

  const handleProjectTemplateSelect = (index, selectedOptionId) => {
    const selectedOption = subDepartment?.allProjectTemplates?.find(option => option.Id === selectedOptionId);

    const updatedPairs = [...subDepartment.WidgetPairs];
    updatedPairs[index].WidgetProjectTemplate = selectedOption;

    if (selectedOption.Id !== subDepartment.DefaultProjectTemplate.Id) {
      updatedPairs[index].SameAsDefaultPair = false;
    }

    setSubDepartment((prev) => ({
      ...prev,
      WidgetPairs: updatedPairs,
    }));
  };

  const handleTaskTypeSelect = (index, selectedOptionId) => {
    const selectedOption = subDepartment?.allTaskTypes?.find(option => option.Id === selectedOptionId);

    const updatedPairs = [...subDepartment.WidgetPairs];
    updatedPairs[index].WidgetTaskType = selectedOption;

    if (selectedOption.Id !== subDepartment.DefaultTaskType.Id) {
      updatedPairs[index].SameAsDefaultPair = false;
    }

    setSubDepartment((prev) => ({
      ...prev,
      WidgetPairs: updatedPairs,
    }));
  };

  const handleDefaultSwitchChange = (index, checked) => {
    const updatedPairs = [...subDepartment.WidgetPairs];
    updatedPairs[index].SameAsDefaultPair = checked;

    if (checked) {
      updatedPairs[index].WidgetProjectTemplate = subDepartment.DefaultProjectTemplate;
      updatedPairs[index].WidgetTaskType = subDepartment.DefaultTaskType;
    }

    setSubDepartment((prev) => ({
      ...prev,
      WidgetPairs: updatedPairs,
    }));
  };

  useEffect(() => {
    const updatedPairs = subDepartment.WidgetPairs.map((pair) => ({
      ...pair,
      WidgetProjectTemplate: pair.SameAsDefaultPair ? subDepartment.DefaultProjectTemplate : pair.WidgetProjectTemplate,
      WidgetTaskType: pair.SameAsDefaultPair ? subDepartment.DefaultTaskType : pair.WidgetTaskType,
    }));

    setSubDepartment((prev) => ({
      ...prev,
      WidgetPairs: updatedPairs,
    }));
  }, [subDepartment.DefaultProjectTemplate, subDepartment.DefaultTaskType]);

  return (
    <div className="sub-department-widget">
      <div className="fixed-add-button">
        <div className="add-button-container">
          <FluentButton
            text="Add Project Template - Task Type Pair"
            color="#0F6CBD"
            onClick={handleAddPair}
            icon={<FluentUiIcon name={'AddRegular'} color={'#0F6CBD'} size={20} />}
          />
        </div>
      </div>

      {subDepartment.WidgetPairs.length === 0 ? (
        <p>No Project Template - Task Type Pairs found. Please add one.</p>
      ) : (
        subDepartment.WidgetPairs.map((pair, index) => (
          <div key={index}>
            <div className="pair-container">
              <FluentField label="Project Template">
                <Dropdown
                  placeholder="Select Project Template"
                  value={pair?.WidgetProjectTemplate?.ProjectType || ""}
                  selectedOptions={[pair?.WidgetProjectTemplate?.Id || ""]}
                  onOptionSelect={(_, data) => handleProjectTemplateSelect(index, data.optionValue)}
                >
                  {subDepartment?.allProjectTemplates?.map((option) => (
                    <Option key={option.Id} value={option.Id}>
                      {option.ProjectType}
                    </Option>
                  ))}
                </Dropdown>
              </FluentField>

              <FluentField label="Task Type">
                <Dropdown
                  placeholder="Select Task Type"
                  value={pair?.WidgetTaskType?.Type || ""}
                  selectedOptions={[pair?.WidgetTaskType?.Id || ""]}
                  onOptionSelect={(_, data) => handleTaskTypeSelect(index, data.optionValue)}
                >
                  {subDepartment?.allTaskTypes.map((option) => (
                    <Option key={option.Id} value={option.Id}>
                      {option.Type}
                    </Option>
                  ))}
                </Dropdown>
              </FluentField>

              <FluentButton
                onClick={() => handleDeletePair(index)}
                icon={<FluentUiIcon name={'DeleteRegular'} color={'red'} size={20} />}
                className="delete-button"
              />
            </div>

            <FluentSwitch
              label="Use Default"
              checked={pair.SameAsDefaultPair}
              onChange={(e) => handleDefaultSwitchChange(index, e.target.checked)}
              size="small"
            />
          </div>
        ))
      )}
    </div>
  );
};

WidgetSettings.propTypes = {
  subDepartment: PropTypes.shape({
    allProjectTemplates: PropTypes.array.isRequired,
    allTaskTypes: PropTypes.array.isRequired,
    WidgetPairs: PropTypes.arrayOf(
      PropTypes.shape({
        WidgetProjectTemplate: PropTypes.object,
        WidgetTaskType: PropTypes.object,
        SameAsDefaultPair: PropTypes.bool,
        Order: PropTypes.number,
      })
    ).isRequired,
    DefaultProjectTemplate: PropTypes.object.isRequired,
    DefaultTaskType: PropTypes.object.isRequired,
  }).isRequired,
  setSubDepartment: PropTypes.func.isRequired,
};

export default WidgetSettings;
